import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Play,
  Archive,
  FileEdit,
  Loader2,
  Timer,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAllProducts } from '@/hooks/useProducts'
import { supabase } from '@/lib/supabase'
import type { Product, ProductStatus } from '@/types/database'
import { cn } from '@/lib/utils'
import { differenceInDays, differenceInHours, format, parseISO } from 'date-fns'

// Calendar helpers
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

function getDaysInMonth(year: number, month: number): Date[] {
  const result: Date[] = []
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  // Add padding days from previous month
  for (let i = 0; i < firstDay.getDay(); i++) {
    const date = new Date(year, month, -i)
    result.unshift(date)
  }

  // Add days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    result.push(new Date(year, month, i))
  }

  // Add padding days for next month to complete the grid
  const remaining = 42 - result.length // 6 rows * 7 days
  for (let i = 1; i <= remaining; i++) {
    result.push(new Date(year, month + 1, i))
  }

  return result
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
}

function isWithinRange(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false
  return date >= start && date <= end
}

const statusColors: Record<ProductStatus, { bg: string; text: string; border: string }> = {
  featured: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  scheduled: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/50' },
  draft: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/50' },
  archived: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50' },
  hidden: { bg: 'bg-zinc-700/20', text: 'text-zinc-500', border: 'border-zinc-700/50' },
}

const statusIcons: Record<ProductStatus, typeof Play> = {
  featured: Play,
  scheduled: Clock,
  draft: FileEdit,
  archived: Archive,
  hidden: Archive,
}

function formatCountdown(endDate: string): string {
  const end = parseISO(endDate)
  const now = new Date()
  const days = differenceInDays(end, now)
  const hours = differenceInHours(end, now) % 24

  if (days > 0) {
    return `${days}d ${hours}h remaining`
  } else if (hours > 0) {
    return `${hours}h remaining`
  } else if (days < 0) {
    const expiredDays = Math.abs(days)
    return `Expired ${expiredDays}d ago`
  }
  return 'Ending soon'
}

function isExpiredFeatured(product: Product): boolean {
  return product.status === 'featured' &&
    !!product.featured_end_date &&
    parseISO(product.featured_end_date) < new Date()
}

function formatTimeUntil(startDate: string): string {
  const start = parseISO(startDate)
  const now = new Date()
  const days = differenceInDays(start, now)

  if (days > 1) {
    return `Starts in ${days} days`
  } else if (days === 1) {
    return 'Starts tomorrow'
  } else if (days === 0) {
    return 'Starts today'
  }
  return 'Ready to go'
}

export function AdminDropsPage() {
  const { products, isLoading, refetch } = useAllProducts()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [mobileView, setMobileView] = useState<'queue' | 'calendar'>('queue')

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const days = getDaysInMonth(year, month)
  const today = new Date()

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1))

  // Get drops that have date ranges
  const dropsWithDates = products.filter(p =>
    p.featured_start_date && p.featured_end_date
  )

  // Group by status for the queue — split featured into live vs expired
  const allFeatured = products.filter(p => p.status === 'featured')
  const liveFeatured = allFeatured.filter(p => !isExpiredFeatured(p))
  const expiredFeatured = allFeatured.filter(p => isExpiredFeatured(p))
  const scheduled = products.filter(p => p.status === 'scheduled')
    .sort((a, b) =>
      new Date(a.featured_start_date!).getTime() - new Date(b.featured_start_date!).getTime()
    )
  const drafts = products.filter(p => p.status === 'draft')
  const [archiving, setArchiving] = useState<string | null>(null)

  async function handleArchiveNow(productId: string) {
    setArchiving(productId)
    await supabase
      .from('products')
      .update({ status: 'archived' })
      .eq('id', productId)
    await refetch()
    setArchiving(null)
  }

  // Check if a day has any drops
  const getDropsForDay = (date: Date): Product[] => {
    return dropsWithDates.filter(p => {
      const start = new Date(p.featured_start_date!)
      const end = new Date(p.featured_end_date!)
      return isWithinRange(date, start, end)
    })
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Drops Calendar | Admin</title>
      </Helmet>

      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Drops Calendar</h1>
            <p className="text-sm text-muted-foreground">
              Manage your weekly drop schedule
            </p>
          </div>
          <Button asChild className="w-full sm:w-auto">
            <Link to="/admin/products/new">
              <Plus className="mr-2 size-4" />
              Schedule New Drop
            </Link>
          </Button>
        </div>

        <div className="flex rounded-lg border bg-card p-1 lg:hidden">
          <Button
            type="button"
            variant={mobileView === 'queue' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setMobileView('queue')}
          >
            Queue
          </Button>
          <Button
            type="button"
            variant={mobileView === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setMobileView('calendar')}
          >
            Calendar
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Calendar */}
          <Card className={cn('lg:col-span-2', mobileView === 'queue' && 'hidden lg:block')}>
            <CardHeader className="space-y-3 pb-4 sm:flex sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Calendar className="size-5" />
                {MONTHS[month]} {year}
              </CardTitle>
              <div className="flex items-center gap-1 self-start sm:self-auto">
                <Button variant="ghost" size="icon" onClick={prevMonth}>
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentDate(new Date())}
                >
                  Today
                </Button>
                <Button variant="ghost" size="icon" onClick={nextMonth}>
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="mb-2 grid grid-cols-7 gap-1">
                {DAYS.map(day => (
                  <div key={day} className="py-2 text-center text-xs font-medium text-muted-foreground">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{day[0]}</span>
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((date, i) => {
                  const isCurrentMonth = date.getMonth() === month
                  const isToday = isSameDay(date, today)
                  const dropsOnDay = getDropsForDay(date)

                  return (
                    <div
                      key={i}
                      className={cn(
                        'relative min-h-20 rounded-md border p-1 text-sm',
                        isCurrentMonth ? 'bg-card' : 'bg-muted/30',
                        isToday && 'border-primary ring-1 ring-primary'
                      )}
                    >
                      <span className={cn(
                        'absolute right-1 top-1 text-xs',
                        isCurrentMonth ? 'text-foreground' : 'text-muted-foreground',
                        isToday && 'font-bold text-primary'
                      )}>
                        {date.getDate()}
                      </span>

                      {/* Drops on this day */}
                      <div className="mt-5 space-y-0.5">
                        {dropsOnDay.slice(0, 2).map(drop => {
                          const colors = statusColors[drop.status]
                          return (
                            <Link
                              key={drop.id}
                              to={`/admin/products/${drop.id}/edit`}
                              className={cn(
                                'block truncate rounded px-1 py-0.5 text-[10px] font-medium transition-opacity hover:opacity-80',
                                colors.bg, colors.text
                              )}
                              title={drop.title}
                            >
                              {drop.title}
                            </Link>
                          )
                        })}
                        {dropsOnDay.length > 2 && (
                          <span className="block text-[10px] text-muted-foreground">
                            +{dropsOnDay.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-4 text-xs">
                {(['featured', 'scheduled', 'draft'] as ProductStatus[]).map(status => {
                  const colors = statusColors[status]
                  const Icon = statusIcons[status]
                  return (
                    <div key={status} className="flex items-center gap-1.5">
                      <div className={cn('size-3 rounded', colors.bg, colors.border, 'border')} />
                      <Icon className={cn('size-3', colors.text)} />
                      <span className="capitalize text-muted-foreground">{status}</span>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Queue Sidebar */}
          <div className={cn('space-y-4', mobileView === 'calendar' && 'hidden lg:block')}>
            {/* Expired Featured — Needs Archiving */}
            {expiredFeatured.length > 0 && (
              <Card className="border-amber-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <AlertTriangle className="size-4 text-amber-500" />
                    Expired — Needs Archiving
                    <Badge className="ml-auto bg-amber-100 text-amber-700">{expiredFeatured.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {expiredFeatured.map(p => (
                      <div
                        key={p.id}
                        className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4"
                      >
                        <Link
                          to={`/admin/products/${p.id}/edit`}
                          className="font-semibold text-amber-600 hover:underline"
                        >
                          {p.title}
                        </Link>
                        {p.featured_end_date && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-amber-500">
                            <Timer className="size-3" />
                            {formatCountdown(p.featured_end_date)}
                          </div>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground">
                          Ended {format(parseISO(p.featured_end_date!), 'MMM d, yyyy')}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3 w-full border-amber-500/50 text-amber-700 hover:bg-amber-500/20"
                          disabled={archiving === p.id}
                          onClick={() => handleArchiveNow(p.id)}
                        >
                          {archiving === p.id ? (
                            <Loader2 className="mr-2 size-3 animate-spin" />
                          ) : (
                            <Archive className="mr-2 size-3" />
                          )}
                          Archive Now
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Currently Live */}
            <Card className={liveFeatured.length > 0 ? 'border-red-500/30' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Play className="size-4 text-red-500" />
                  Live Now
                  {liveFeatured.length > 0 && (
                    <Badge variant="destructive" className="ml-auto">Active</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {liveFeatured.length > 0 ? (
                  <div className="space-y-3">
                    {liveFeatured.map(p => (
                      <Link
                        key={p.id}
                        to={`/admin/products/${p.id}/edit`}
                        className="block rounded-lg border border-red-500/30 bg-red-500/10 p-4 transition-colors hover:bg-red-500/20"
                      >
                        <p className="font-semibold text-red-500">{p.title}</p>
                        {p.featured_end_date && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-red-400">
                            <Timer className="size-3" />
                            {formatCountdown(p.featured_end_date)}
                          </div>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground">
                          Ends {format(parseISO(p.featured_end_date!), 'MMM d, yyyy')}
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No active featured deal</p>
                )}
              </CardContent>
            </Card>

            {/* Up Next Queue */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="size-4 text-blue-500" />
                  Up Next
                  {scheduled.length > 0 && (
                    <Badge variant="outline" className="ml-auto">{scheduled.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scheduled.length > 0 ? (
                  <div className="space-y-2">
                    {scheduled.map((p, i) => (
                      <Link
                        key={p.id}
                        to={`/admin/products/${p.id}/edit`}
                        className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                      >
                        <span className="flex size-6 items-center justify-center rounded-full bg-blue-500/20 text-xs font-bold text-blue-500">
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{p.title}</p>
                          <p className="text-xs text-blue-500">
                            {formatTimeUntil(p.featured_start_date!)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(p.featured_start_date!), 'MMM d')} - {format(parseISO(p.featured_end_date!), 'MMM d')}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No scheduled drops</p>
                )}
              </CardContent>
            </Card>

            {/* Drafts */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <FileEdit className="size-4 text-zinc-400" />
                  Drafts
                  {drafts.length > 0 && (
                    <Badge variant="secondary" className="ml-auto">{drafts.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {drafts.length > 0 ? (
                  <div className="space-y-2">
                    {drafts.slice(0, 5).map(p => (
                      <Link
                        key={p.id}
                        to={`/admin/products/${p.id}/edit`}
                        className="flex items-center justify-between rounded-lg border p-2 text-sm transition-colors hover:bg-muted/50"
                      >
                        <span className="truncate">{p.title}</span>
                        <Button variant="ghost" size="sm" className="h-6 text-xs">
                          Schedule
                        </Button>
                      </Link>
                    ))}
                    {drafts.length > 5 && (
                      <Link to="/admin/products" className="block text-xs text-muted-foreground hover:text-foreground">
                        +{drafts.length - 5} more drafts →
                      </Link>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No drafts</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
